import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { InputNumber, Input, Alert } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { datePickerToApi, getMoment } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { forceReloadCreditNote } from '@hooks/API/UseCreditNotes';
import { useTransaction } from '@hooks/API/UseTransaction';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import { NwSaveButton } from '@components/Gui/NwButton';
import AccountCodeSelector from '@components/Gui/NwForm/AccountCodeSelector';
import CustomDatePicker from '@components/Gui/NwForm/CustomDatePicker';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 1rem;

    .info-box {
        background-color: #f4f4f4;
        padding: 10px;
        border-radius: 5px;

        .info-box-header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 1rem;
            .info-box-icon {
                font-size: 1.5rem;
                color: #007bff;
            }
            .info-box-content {
                flex: 1;
            }
        }

        ul {
            padding-inline-start: 1.5rem;
        }
    }

    .form-group {
        margin-bottom: 0.75rem;

        h2 {
            font-size: ${(props) => props.theme.FontSizeBase};
            font-weight: 600;
        }

        .form-line {
            margin-bottom: 0.75rem;
            h3 {
                font-size: ${(props) => props.theme.FontSizeBase};
                font-weight: 600;
            }
            .form-line-item {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 1rem;
                margin-bottom: 0.75rem;

                label {
                    font-size: ${(props) => props.theme.FontSizeBase};
                    font-weight: 300;
                    text-transform: uppercase;
                    min-width: 12rem;

                    &.tax-label {
                        min-width: 2rem;
                    }
                }
            }
        }
        small {
            display: block;
            padding: 5px 0;
            font-size: 0.9rem;
            border-bottom: ${(props) => props.theme.LighterBorder};
        }
    }
`;

const TextArea = Input.TextArea;

const CreateCreditNoteTransactionDrawer = ({
    creditNoteID,
    creditNoteType,
    transactionID,
    statementType,
    statementID,
    transactionFamily,
    creditNoteCurrency,
    onTransactionCreated,
}) => {
    const configuration = useConfigurations();

    const schemas = [];
    if (configuration && configuration.COA) {
        const COAjs = JSON.parse(configuration.COA);
        if (COAjs.schemas && COAjs.schemas.length > 0) {
            schemas.push(...COAjs.schemas);
        }
    }
    const enableCOA = schemas.length > 0;

    const temporaryDisableCOA = false;

    const defaultCurrency = configuration.defaultCurrency;
    const isDefaultCurrency = creditNoteCurrency === defaultCurrency;

    const defaultTransaction = {
        Title: '',
        Description: '',
        RefDate: null,
        OriginalTransactionID: transactionID,
        ClientStatementID: null,
        ClientAmount: 0,
        //ClientAmountVat: 0,
        ClientAmountTax: 0,
        ClientAmountTax2: 0,
        ClientAccountCode: '',

        ClientCommissionStatementID: null,
        ClientCommissionAmount: 0,
        //ClientCommissionAmountVat: 0,
        ClientCommissionAmountTax: 0,
        ClientCommissionAmountTax2: 0,
        ClientCommissionAccountCode: '',

        ModelStatementID: null,
        ModelAmount: 0,
        //ModelAmountVat: 0,
        ModelAmountTax: 0,
        ModelAmountTax2: 0,

        ModelCommissionStatementID: null,
        ModelCommissionAmount: 0,
        //ModelCommissionAmountVat: 0,
        ModelCommissionAmountTax: 0,
        ModelCommissionAmountTax2: 0,

        ServiceStatementID: null,
        ServiceAmount: 0,
        //ServiceAmountVat: 0,
        ServiceAmountTax: 0,
        ServiceAmountTax2: 0,

        CreditNoteAgencyAmounts: [],
    };

    const [newTransaction, setNewTransaction] = useState(defaultTransaction);

    const { data: transactionData, isFetching: loadingTransaction } = useTransaction(transactionID, transactionFamily);
    const [modelName, setModelName] = useState('');
    const [serviceName, setServiceName] = useState('');
    const [clientName, setClientName] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [serverError, setServerError] = useState('');

    const validateClientAmount = () => {
        const errors = {};
        // Only validate if the ClientAmount field is rendered (ClientStatementID exists)
        if (newTransaction.ClientStatementID && newTransaction.ClientAmount <= 0) {
            errors.ClientAmount = 'Amount must be greater than 0';
        }
        // Only validate if the ClientCommissionAmount field is rendered (ClientCommissionStatementID exists)
        if (newTransaction.ClientCommissionStatementID && newTransaction.ClientCommissionAmount <= 0) {
            errors.ClientCommissionAmount = 'Amount must be greater than 0';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const isFormValid = () => {
        // Check ClientAmount validation if the field is rendered
        if (newTransaction.ClientStatementID && newTransaction.ClientAmount <= 0) {
            return false;
        }
        // Check ClientCommissionAmount validation if the field is rendered
        if (newTransaction.ClientCommissionStatementID && newTransaction.ClientCommissionAmount <= 0) {
            return false;
        }
        return true; // Form is valid if all rendered fields are valid
    };

    const handleClientAmountBlur = () => {
        // Only validate if the ClientAmount field is rendered
        if (newTransaction.ClientStatementID) {
            validateClientAmount();
        }
    };

    const handleClientCommissionAmountBlur = () => {
        // Only validate if the ClientCommissionAmount field is rendered
        if (newTransaction.ClientCommissionStatementID) {
            validateClientAmount();
        }
    };

    const saveTransaction = async () => {
        setServerError('');
        const requestObject = {
            CreditNoteType: creditNoteType,
            CreditNoteID: creditNoteID,
            Rows: [newTransaction],
        };
        Axios.post('Transactions/CreditNoteRow', requestObject)
            .then((sent) => {
                onTransactionCreated();
            })
            .catch((error) => {
                console.error('error', error);
                setServerError(error.message);
            });
    };

    // {AgencyStatementID: null,Amount: 0,},

    const getNewTransactionTitle = (transactionData) => {
        let title = 'ADJ - ';
        switch (transactionData.Family) {
            case 'JobFee':
                if (statementType === 'client') {
                    const selectedStatement = transactionData.ClientStatements.find((s) => s.ID === statementID);
                    if (selectedStatement.isCommission) {
                        title += 'Agency Fee';
                    } else {
                        title += 'Job Fee';
                    }
                } else {
                    title += 'Job Fee';
                }
                break;
            default:
                title += transactionData.Family;
                break;
        }
        if (transactionData.Title) {
            title += ' - ' + transactionData.Title;
        }
        return title;
    };

    useEffect(() => {
        const nt = { ...defaultTransaction };
        if (transactionData) {
            if (transactionData.ModelID) {
                setModelName(getContactName(transactionData.ModelID));
            }
            if (transactionData.ServiceID) {
                setServiceName(getContactName(transactionData.ServiceID));
            }
            if (transactionData.ClientID) {
                setClientName(getContactName(transactionData.ClientID));
            }

            nt.OriginalTransactionID = transactionData.ID;
            nt.Title = getNewTransactionTitle(transactionData);
            nt.Description = transactionData.Description;
            nt.RefDate = transactionData.RefDate;

            if (statementType === 'client') {
                const selectedStatement = transactionData.ClientStatements.find((s) => s.ID === statementID);
                if (selectedStatement.isCommission) {
                    nt.ClientCommissionStatementID = selectedStatement.ID;
                    nt.ClientCommissionAmount = isDefaultCurrency
                        ? Math.abs(selectedStatement.AmountDef)
                        : Math.abs(selectedStatement.Amount);
                    nt.ClientCommissionAmountTax = isDefaultCurrency
                        ? Math.abs(selectedStatement.TaxDef)
                        : Math.abs(selectedStatement.TaxAmount);
                    nt.ClientCommissionAmountTax2 = isDefaultCurrency
                        ? Math.abs(selectedStatement.TaxDef2)
                        : Math.abs(selectedStatement.TaxAmount2);

                    nt.ClientCommissionAccountCode = selectedStatement.AccountCode;
                } else {
                    nt.ClientStatementID = selectedStatement.ID;
                    nt.ClientAmount = isDefaultCurrency
                        ? Math.abs(selectedStatement.AmountDef)
                        : Math.abs(selectedStatement.Amount);
                    nt.ClientAmountTax = isDefaultCurrency
                        ? Math.abs(selectedStatement.TaxDef)
                        : Math.abs(selectedStatement.TaxAmount);
                    nt.ClientAmountTax2 = isDefaultCurrency
                        ? Math.abs(selectedStatement.TaxDef2)
                        : Math.abs(selectedStatement.TaxAmount2);
                    nt.ClientAccountCode = selectedStatement.AccountCode;

                    const modelSelectedStatement = transactionData.ModelStatements.find((s) => !s.isCommission);
                    if (modelSelectedStatement) {
                        nt.ModelStatementID = modelSelectedStatement.ID;
                        nt.ModelAmount = isDefaultCurrency
                            ? Math.abs(modelSelectedStatement.AmountDef)
                            : Math.abs(modelSelectedStatement.Amount);
                        nt.ModelAmountTax = isDefaultCurrency
                            ? Math.abs(modelSelectedStatement.TaxDef)
                            : Math.abs(modelSelectedStatement.TaxAmount);
                        nt.ModelAmountTax2 = isDefaultCurrency
                            ? Math.abs(modelSelectedStatement.TaxDef2)
                            : Math.abs(modelSelectedStatement.TaxAmount2);
                    }

                    const modelSelectedCommissionStatement = transactionData.ModelStatements.find(
                        (s) => s.isCommission
                    );
                    if (modelSelectedCommissionStatement) {
                        nt.ModelCommissionStatementID = modelSelectedCommissionStatement.ID;
                        nt.ModelCommissionAmount = isDefaultCurrency
                            ? Math.abs(modelSelectedCommissionStatement.AmountDef)
                            : Math.abs(modelSelectedCommissionStatement.Amount);
                        nt.ModelCommissionAmountTax = isDefaultCurrency
                            ? Math.abs(modelSelectedCommissionStatement.TaxDef)
                            : Math.abs(modelSelectedCommissionStatement.TaxAmount);
                        nt.ModelCommissionAmountTax2 = isDefaultCurrency
                            ? Math.abs(modelSelectedCommissionStatement.TaxDef2)
                            : Math.abs(modelSelectedCommissionStatement.TaxAmount2);
                    }

                    const serviceSelectedStatement = transactionData.ServiceStatements.find((s) => !s.isCommission);
                    if (serviceSelectedStatement) {
                        nt.ServiceStatementID = serviceSelectedStatement.ID;
                        nt.ServiceAmount = isDefaultCurrency
                            ? Math.abs(serviceSelectedStatement.AmountDef)
                            : Math.abs(serviceSelectedStatement.Amount);
                        nt.ServiceAmountTax = isDefaultCurrency
                            ? Math.abs(serviceSelectedStatement.TaxDef)
                            : Math.abs(serviceSelectedStatement.TaxAmount);
                        nt.ServiceAmountTax2 = isDefaultCurrency
                            ? Math.abs(serviceSelectedStatement.TaxDef2)
                            : Math.abs(serviceSelectedStatement.TaxAmount2);
                    }

                    if (transactionData.AgencyStatements.length > 0) {
                        for (const agst of transactionData.AgencyStatements) {
                            const agstRow = {
                                AgencyStatementID: agst.ID,
                                Amount: isDefaultCurrency ? Math.abs(agst.AmountDef) : Math.abs(agst.Amount),
                                AgencyName: getContactName(agst.ContactID),
                                AmountTax: isDefaultCurrency ? Math.abs(agst.TaxDef) : Math.abs(agst.TaxAmount),
                                AmountTax2: isDefaultCurrency ? Math.abs(agst.TaxDef2) : Math.abs(agst.TaxAmount2),
                            };
                            nt.CreditNoteAgencyAmounts.push(agstRow);
                        }
                    }
                }
            }
        }
        setNewTransaction(nt);
    }, [transactionData]);

    const getAmountLabel = useCallback(
        (type, isCommission) => {
            if (!transactionData) {
                return 'amount';
            }
            let family = '';
            if (transactionData) {
                family = transactionData.Family;
                switch (family.toLowerCase()) {
                    case 'jobfee':
                        if (type === 'model') {
                            if (isCommission) {
                                return 'agency commission';
                            }
                            return 'model fee';
                        }
                        if (type === 'client') {
                            if (isCommission) {
                                return 'agency fee';
                            }
                        }
                        if (type === 'agency') {
                            return 'mother agency commission';
                        }
                        return 'fee';
                    case 'jobexpense':
                        if (type === 'service') {
                            return 'cost';
                        }
                        return 'expense';
                    case 'jobrefund':
                        if (type === 'model') {
                            return 'refund';
                        }
                        return 'expense';
                    case 'agfee':
                        return 'agency fee';
                    default:
                        return 'amount';
                }
            }
            return type;
        },
        [transactionData]
    );

    if (loadingTransaction || !newTransaction) {
        return <Loading />;
    }

    const updateNewTransaction = (field, value) => {
        const newState = { ...newTransaction };
        newState[field] = value;
        setNewTransaction(newState);
    };

    const updateNewTransactionNumber = (field, value) => {
        const newState = { ...newTransaction };
        newState[field] = value ? Number(value) : 0;
        setNewTransaction(newState);
    };

    const updateNewTransactionAgency = (value, index) => {
        const newState = { ...newTransaction };
        newState.CreditNoteAgencyAmounts[index].Amount = value;
        setNewTransaction(newState);
    };

    const updateNewTransactionAgencyTax = (value, index) => {
        const newState = { ...newTransaction };
        newState.CreditNoteAgencyAmounts[index].AmountTax = value;
        setNewTransaction(newState);
    };

    const updateNewTransactionAgencyTax2 = (value, index) => {
        const newState = { ...newTransaction };
        newState.CreditNoteAgencyAmounts[index].AmountTax2 = value;
        setNewTransaction(newState);
    };

    const updateNewTransactionDate = (value) => {
        const newState = { ...newTransaction };
        newState.RefDate = datePickerToApi(value);
        setNewTransaction(newState);
    };

    return (
        <Wrapper>
            <div className="form-group">
                <div className="form-line">
                    <h3>Title</h3>
                    <div className="form-line-item">
                        <Input
                            maxLength={255}
                            value={newTransaction.Title}
                            onChange={(e) => updateNewTransaction('Title', e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-line">
                    <h3>Description</h3>
                    <div className="form-line-item">
                        <TextArea
                            maxLength={2500}
                            value={newTransaction.Description}
                            onChange={(e) => updateNewTransaction('Description', e.target.value)}
                        />
                    </div>
                </div>
                <div className="form-line">
                    <h3>Date</h3>
                    <div className="form-line-item">
                        <CustomDatePicker
                            value={newTransaction.RefDate ? getMoment(newTransaction.RefDate) : null}
                            onChange={(value) => updateNewTransactionDate(value)}
                            format="MMM DD, YYYY"
                        />
                    </div>
                </div>
            </div>
            <div className="info-box">
                <div className="info-box-header">
                    <div className="info-box-icon">
                        <NwIcon icon={byPrefixAndName.fasl['info-circle']} />
                    </div>
                    <div className="info-box-content">
                        <strong>Adjust amounts to Write Off</strong>
                        <br />
                        <p>
                            Select the amounts you want to write off for each category. By default, the full amount is
                            written off. You can modify the values as needed.
                        </p>
                    </div>
                </div>
            </div>
            {(newTransaction.ClientStatementID || newTransaction.ClientCommissionStatementID) && (
                <div className="form-group">
                    <h2>Client: {clientName}</h2>
                    {newTransaction.ClientStatementID && (
                        <div className="form-line">
                            <div className="form-line-item">
                                <label>{getAmountLabel('client', false)}</label>
                                <InputNumber
                                    value={newTransaction.ClientAmount}
                                    onChange={(value) => {
                                        updateNewTransactionNumber('ClientAmount', value);
                                    }}
                                    onBlur={handleClientAmountBlur}
                                />
                                {validationErrors.ClientAmount && (
                                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                        {validationErrors.ClientAmount}
                                    </div>
                                )}
                                {newTransaction.ClientAmountTax > 0 && (
                                    <>
                                        <label className="tax-label">tax</label>
                                        <InputNumber
                                            value={newTransaction.ClientAmountTax}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ClientAmountTax', value);
                                            }}
                                        />
                                    </>
                                )}
                                {newTransaction.ClientAmountTax2 > 0 && (
                                    <>
                                        <label className="tax-label">tax2</label>
                                        <InputNumber
                                            value={newTransaction.ClientAmountTax2}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ClientAmountTax2', value);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                            {enableCOA && !temporaryDisableCOA && (
                                <div className="form-line-item">
                                    <label>account code</label>
                                    <AccountCodeSelector
                                        value={newTransaction.ClientAccountCode}
                                        onChange={(value) => {
                                            updateNewTransaction('ClientAccountCode', value);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {newTransaction.ClientCommissionStatementID && (
                        <div className="form-line">
                            <div className="form-line-item">
                                <label>{getAmountLabel('client', true)}</label>
                                <InputNumber
                                    value={newTransaction.ClientCommissionAmount}
                                    onChange={(value) => {
                                        updateNewTransactionNumber('ClientCommissionAmount', value);
                                    }}
                                    onBlur={handleClientCommissionAmountBlur}
                                />
                                {validationErrors.ClientCommissionAmount && (
                                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                        {validationErrors.ClientCommissionAmount}
                                    </div>
                                )}
                                {newTransaction.ClientCommissionAmountTax > 0 && (
                                    <>
                                        <label className="tax-label">tax</label>
                                        <InputNumber
                                            value={newTransaction.ClientCommissionAmountTax}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ClientCommissionAmountTax', value);
                                            }}
                                        />
                                    </>
                                )}
                                {newTransaction.ClientCommissionAmountTax2 > 0 && (
                                    <>
                                        <label className="tax-label">tax2</label>
                                        <InputNumber
                                            value={newTransaction.ClientCommissionAmountTax2}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ClientCommissionAmountTax2', value);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                            {enableCOA && !temporaryDisableCOA && (
                                <div className="form-line-item">
                                    <label>account code</label>
                                    <AccountCodeSelector
                                        value={newTransaction.ClientCommissionAccountCode}
                                        onChange={(value) => {
                                            updateNewTransaction('ClientCommissionAccountCode', value);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <small>Enter the amount to be written off from the client's fee.</small>
                </div>
            )}
            {(newTransaction.ModelStatementID || newTransaction.ModelCommissionStatementID) && (
                <div className="form-group">
                    <h2>Model: {modelName}</h2>
                    {newTransaction.ModelStatementID && (
                        <div className="form-line">
                            <div className="form-line-item">
                                <label>{getAmountLabel('model', false)}</label>
                                <InputNumber
                                    value={newTransaction.ModelAmount}
                                    onChange={(value) => {
                                        updateNewTransactionNumber('ModelAmount', value);
                                    }}
                                />
                                {newTransaction.ModelAmountTax > 0 && (
                                    <>
                                        <label className="tax-label">tax</label>
                                        <InputNumber
                                            value={newTransaction.ModelAmountTax}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ModelAmountTax', value);
                                            }}
                                        />
                                    </>
                                )}
                                {newTransaction.ModelAmountTax2 > 0 && (
                                    <>
                                        <label className="tax-label">tax2</label>
                                        <InputNumber
                                            value={newTransaction.ModelAmountTax2}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ModelAmountTax2', value);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {newTransaction.ModelCommissionStatementID && newTransaction.ModelCommissionAmount > 0 && (
                        <div className="form-line">
                            <div className="form-line-item">
                                <label>{getAmountLabel('model', true)}</label>
                                <InputNumber
                                    value={newTransaction.ModelCommissionAmount}
                                    onChange={(value) => {
                                        updateNewTransactionNumber('ModelCommissionAmount', value);
                                    }}
                                />
                                {newTransaction.ModelCommissionAmountTax > 0 && (
                                    <>
                                        <label className="tax-label">tax</label>
                                        <InputNumber
                                            value={newTransaction.ModelCommissionAmountTax}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ModelCommissionAmountTax', value);
                                            }}
                                        />
                                    </>
                                )}
                                {newTransaction.ModelCommissionAmountTax2 > 0 && (
                                    <>
                                        <label className="tax-label">tax2</label>
                                        <InputNumber
                                            value={newTransaction.ModelCommissionAmountTax2}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ModelCommissionAmountTax2', value);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <small>
                        Model Fee & Agency Commission: Specify the amount to be written off for the model's fee and the
                        agency's commission (if applicable).
                    </small>
                </div>
            )}
            {newTransaction.ServiceStatementID && (
                <div className="form-group">
                    <h2>Service: {serviceName}</h2>
                    {newTransaction.ServiceStatementID && (
                        <div className="form-line">
                            <div className="form-line-item">
                                <label>{getAmountLabel('service', false)}</label>
                                <InputNumber
                                    value={newTransaction.ServiceAmount}
                                    onChange={(value) => {
                                        updateNewTransactionNumber('ServiceAmount', value);
                                    }}
                                />
                                {newTransaction.ServiceAmountTax > 0 && (
                                    <>
                                        <label className="tax-label">tax</label>
                                        <InputNumber
                                            value={newTransaction.ServiceAmountTax}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ServiceAmountTax', value);
                                            }}
                                        />
                                    </>
                                )}
                                {newTransaction.ServiceAmountTax2 > 0 && (
                                    <>
                                        <label className="tax-label">tax2</label>
                                        <InputNumber
                                            value={newTransaction.ServiceAmountTax2}
                                            onChange={(value) => {
                                                updateNewTransactionNumber('ServiceAmountTax2', value);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <small>Specify the amount to be written off for the services cost.</small>
                </div>
            )}
            {newTransaction.CreditNoteAgencyAmounts.length > 0 && (
                <div className="form-group">
                    <h2>Agencies</h2>
                    {newTransaction.CreditNoteAgencyAmounts.length > 0 && (
                        <div className="form-line">
                            {newTransaction.CreditNoteAgencyAmounts.map((ag, index) => (
                                <>
                                    <h2>{ag.AgencyName}</h2>
                                    <div className="form-line-item">
                                        <label>{getAmountLabel('agency', false)}</label>
                                        <InputNumber
                                            value={ag.Amount}
                                            onChange={(value) => {
                                                updateNewTransactionAgency(value, index);
                                            }}
                                        />
                                        {ag.AmountTax > 0 && (
                                            <>
                                                <label className="tax-label">tax</label>
                                                <InputNumber
                                                    value={ag.AmountTax}
                                                    onChange={(value) => {
                                                        updateNewTransactionAgencyTax(value, index);
                                                    }}
                                                />
                                            </>
                                        )}
                                        {ag.AmountTax2 > 0 && (
                                            <>
                                                <label className="tax-label">tax2</label>
                                                <InputNumber
                                                    value={ag.AmountTax2}
                                                    onChange={(value) => {
                                                        updateNewTransactionAgencyTax2(value, index);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </>
                            ))}
                        </div>
                    )}
                    <small>Agencies: Enter the amount to be written off from the mother agency commission</small>
                </div>
            )}
            <p>
                <NwSaveButton onClick={saveTransaction} label="save" disabled={!isFormValid()} />
            </p>
            {serverError && <Alert message={serverError} type="error" showIcon />}
        </Wrapper>
    );
};

export default CreateCreditNoteTransactionDrawer;
